import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IHasSourceIdentifier,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Practice, hasMergeConflicts } from '@principle-theorem/principle-core';
import { ISterilisationRecord } from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  Timestamp,
  WithRef,
  asDocRef,
  getError,
  resolveSequentially,
  runTransaction,
  safeCombineLatest,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslationMapHandler } from '../../translation-map';
import { PreloadTranslationMap } from '../../translation-map/preload-translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';
import { PatientIdFilter } from '../filters/patient-id-filter';

export const STERILISATION_RECORD_RESOURCE_TYPE = 'sterilisationRecord';

export const STERILISATION_RECORD_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: STERILISATION_RECORD_RESOURCE_TYPE,
    label: 'Sterilisation Records',
    description: ``,
  },
});

export interface ISterilisationRecordBaseJobData<
  PatientRecord extends ISourceEntityTypes,
> {
  sourcePatient: IGetRecordResponse<PatientRecord>;
}

export interface ISterilisationRecordMigrationData {
  sterilisationRecords: (ISterilisationRecord &
    IHasSourceIdentifier & {
      createdAt: Timestamp;
    })[];
}

export interface ISterilisationRecordSuccessData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  sterilisationRefs: DocumentReference<ISterilisationRecord>[];
}

export abstract class BasePatientSterilisationDestinationEntity<
  PatientRecord extends ISourceEntityTypes,
  JobData extends ISterilisationRecordBaseJobData<PatientRecord>,
> extends BaseDestinationEntity<
  ISterilisationRecordSuccessData,
  JobData,
  ISterilisationRecordMigrationData
> {
  destinationEntity = STERILISATION_RECORD_DESTINATION_ENTITY;
  abstract patientSourceEntity: ISourceEntityHandler<PatientRecord>;

  override filters = [
    new PatientIdFilter<JobData>((jobData) =>
      this.patientSourceEntity
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  get sourceCountComparison(): ISourceEntityHandler<PatientRecord> {
    return this.patientSourceEntity;
  }

  sourceCountDataAccessor(data: JobData): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<ISterilisationRecordSuccessData>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return safeCombineLatest(
      record.data.sterilisationRefs.map((sterilisationRef) =>
        Firestore.getDoc(sterilisationRef)
      )
    ).pipe(
      map((sterilisationRecords) => [
        ...sterilisationRecords.map((sterilisationRecord) => ({
          label: `Sterilisation Record`,
          data: sterilisationRecord,
        })),
      ])
    );
  }

  getDestinationEntityRecordUid(data: JobData): string {
    return data.sourcePatient.record.uid;
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: ISterilisationRecordMigrationData
  ): Promise<ISterilisationRecordMigrationData | undefined> {
    const existingSterilisationRecords: (ISterilisationRecord &
      IHasSourceIdentifier & { createdAt: Timestamp })[] = [];

    const sterilisationMergeConflicts = await resolveSequentially(
      data.sterilisationRecords,
      async (sterilisationRecord) => {
        const sterilisationRecordRef = await translationMap.getDestination(
          sterilisationRecord.sourceIdentifier,
          STERILISATION_RECORD_RESOURCE_TYPE
        );

        if (!sterilisationRecordRef) {
          return false;
        }

        try {
          const existingSterilisationRecord = await Firestore.getDoc(
            asDocRef<ISterilisationRecord>(sterilisationRecordRef)
          );
          existingSterilisationRecords.push({
            ...existingSterilisationRecord,
            sourceIdentifier: sterilisationRecord.sourceIdentifier,
          });

          return hasMergeConflicts(
            sterilisationRecord,
            existingSterilisationRecord
          );
        } catch {
          return;
        }
      }
    );

    if (sterilisationMergeConflicts.some((mergeConflict) => mergeConflict)) {
      return {
        ...data,
        sterilisationRecords: existingSterilisationRecords,
      };
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: JobData,
    _migrationData: ISterilisationRecordMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: JobData,
    migrationData: ISterilisationRecordMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const sterilisationRefs = await runTransaction(async (transaction) =>
        resolveSequentially(
          migrationData.sterilisationRecords,
          async (sterilisationRecord) => {
            const sterilisationRecordCol = Practice.sterilisationRecordCol({
              ref: sterilisationRecord.practice.ref,
            });
            const sterilisationDestinationRef =
              await PreloadTranslationMap.preload(
                translationMap,
                STERILISATION_RECORD_RESOURCE_TYPE,
                sterilisationRecordCol,
                sterilisationRecord.sourceIdentifier
              );

            const sterilisationRef = await FirestoreMigrate.upsertDoc(
              sterilisationRecordCol,
              sterilisationRecord,
              sterilisationDestinationRef,
              transaction
            );

            return sterilisationRef;
          }
        )
      );

      return this._buildSuccessResponse(data.sourcePatient, sterilisationRefs);
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  buildSkippedResponse(
    data: JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  protected _buildSuccessResponse(
    patientRecord: IGetRecordResponse<PatientRecord>,
    sterilisationRefs: DocumentReference<ISterilisationRecord>[]
  ): IDestinationEntityRecord<ISterilisationRecordSuccessData> {
    return {
      uid: patientRecord.record.uid,
      label: patientRecord.record.label,
      data: {
        sourceRef: patientRecord.record.id,
        sterilisationRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patientRecord.record.id,
      migratedAt: toTimestamp(),
    };
  }

  protected _buildErrorResponse(
    patient: IGetRecordResponse<PatientRecord>,
    errorMessage?: string
  ): IDestinationEntityRecord<ISterilisationRecordSuccessData> &
    FailedDestinationEntityRecord {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.record.id,
      errorMessage:
        errorMessage ?? 'Missing required properties for sterilisation',
      failData: {},
    };
  }
}
